import { ee } from '../../services/earth-engine';

/**
 * Holding part of the training data back from the fit, and reporting the error by
 * depth band.
 * A model measured on the same points it was fitted on reports how well it memorised them, not how
 * well it predicts - and the better the model, the more flattering that number. Trees make this
 * worse than a two-coefficient regression does, which is why the split matters before the Random
 * Forest and Gradient Boost models arrive rather than after.
 * The split has to be of the *grid*, not of the points. A survey drops several soundings inside one
 * 30 m pixel - here about five - and the model reads the imagery, which has one value per pixel. Set
 * aside 30% of the points and nearly every pixel still keeps a sounding on the training side, so the
 * model is fitted on a pixel and then scored on that same pixel through a different sounding. It is
 * the same pixel of the same image: the error barely moves, and the number flatters again. Holding
 * back whole pixels is the only division where the validation side is imagery the fit never saw.
 * The error also is not one number. Satellite bathymetry is accurate in shallow water and degrades
 * with depth until the bottom stops being visible at all, so a single RMSE averages a good result
 * and a useless one into a mediocre one. Reported in 2 m bands, where the model stops working shows
 * up as a column instead of being hidden in the average.
 */

/**
 * @constant TRAIN_FRACTION
 * @description Share of the training points used to fit; the rest is held back for validation.
 * @memberof module:Bathymetry
 */
export const TRAIN_FRACTION = 0.7;

/**
 * @constant SPLIT_SEED
 * @description Seed for the draw. Fixed on purpose: the same training file has to produce the same
 * split every run, or re-running the analysis would quietly report a different error, and a session
 * reopened from a file would not be the one that was exported.
 * @memberof module:Bathymetry
 */
export const SPLIT_SEED = 42;

/**
 * @constant DEPTH_BIN_SIZE
 * @description Width of each depth band of the error report, in metres.
 * @memberof module:Bathymetry
 */
export const DEPTH_BIN_SIZE = 2;

/**
 * @constant DEPTH_BIN_PROPERTY
 * @description Name of the column holding which band a point falls in.
 * @memberof module:Bathymetry
 */
export const DEPTH_BIN_PROPERTY = 'depth_bin';

/**
 * @function splitMasks
 * @description The train/validation partition, as two masks over the analysis grid.
 * A deterministic random image is reprojected onto the grid the analysis runs on, so every pixel of
 * that grid draws exactly one number and lands wholly on one side. Masking the predictors with
 * `train` keeps the fit inside its own pixels; masking the reference with `validation` scores only
 * pixels the fit never read.
 * The seed is fixed for the same reason as before: re-running an analysis must not quietly report a
 * different error, and a session reopened from a file must be the one that was exported.
 * @param {Object} options
 * @param {Number} options.scale - Pixel scale of the analysis, in metres.
 * @param {String} options.crs - CRS of the analysis grid.
 * @param {Number} [options.fraction] - Share of the grid kept for fitting.
 * @param {Number} [options.seed] - Seed for the draw.
 * **Called by:
 * @see {BathymetryBandsAndAlgorithmSelection} - masks the predictors before sampling.
 * @see {ProcessingPage} - masks the reference before measuring.
 * @memberof module:Bathymetry
 * @returns {Object} `{train, validation}`, two complementary mask images.
 */
export const splitMasks = ({ scale, crs, fraction = TRAIN_FRACTION, seed = SPLIT_SEED }) => {
  const draw = ee.Image.random(seed).reproject({ crs, scale });
  return { train: draw.lt(fraction), validation: draw.gte(fraction) };
};

/**
 * @function depthBinLabel
 * @description Reads a band index back as the depth range it stands for.
 * @param {Number} bin - Band index, as {@link withDepthBin} assigns it.
 * @param {Number} [binSize] - Band width, in metres.
 * **Called by:
 * @see {mergeDepthBinGroups}
 * @memberof module:Bathymetry
 * @returns {String} e.g. `4-6 m`.
 */
export const depthBinLabel = (bin, binSize = DEPTH_BIN_SIZE) => `${bin * binSize}-${(bin + 1) * binSize} m`;

/**
 * @function withDepthBin
 * @description Tags every point with the depth band it falls in.
 * Bands are measured on the absolute depth, so the report reads in positive metres whichever sign
 * convention the training file uses.
 * @param {Object} collection - Paired points carrying the reference depth.
 * @param {Number} [binSize] - Band width, in metres.
 * @param {String} [depthProperty] - Column holding the reference depth.
 * **Called by:
 * @see {errorByDepthBin}
 * @memberof module:Bathymetry
 * @returns {Object} The collection with {@link DEPTH_BIN_PROPERTY} added.
 */
export const withDepthBin = (collection, binSize = DEPTH_BIN_SIZE, depthProperty = 'or_depth') =>
  ee
    .FeatureCollection(collection)
    .map((feature) =>
      ee
        .Feature(feature)
        .set(DEPTH_BIN_PROPERTY, ee.Number(ee.Feature(feature).get(depthProperty)).abs().divide(binSize).floor()),
    );

/**
 * @function mergeDepthBinGroups
 * @description Joins the three grouped reductions into one row per depth band, ordered from the
 * shallow end down.
 * Kept apart from the Earth Engine call so the arithmetic that turns a mean squared error into an
 * RMSE can be read, and tested, without a round trip.
 * @param {Object} groups - `{squared, absolute, bias}`, each an evaluated grouped reduction.
 * @param {Number} [binSize] - Band width, in metres.
 * **Called by:
 * @see {errorByDepthBin}
 * @memberof module:Bathymetry
 * @returns {Array<Object>} `{bin, label, n, rmse, mae, bias}` per band.
 */
export const mergeDepthBinGroups = (groups, binSize = DEPTH_BIN_SIZE) => {
  const byBin = new Map();
  const at = (bin) => {
    if (!byBin.has(bin))
      byBin.set(bin, { bin, label: depthBinLabel(bin, binSize), n: 0, rmse: null, mae: null, bias: null });
    return byBin.get(bin);
  };

  (groups?.squared?.groups || []).forEach((group) => {
    const row = at(group[DEPTH_BIN_PROPERTY]);
    row.n = group.count ?? 0;
    row.rmse = group.mean != null ? Math.sqrt(group.mean) : null;
  });
  (groups?.absolute?.groups || []).forEach((group) => {
    at(group[DEPTH_BIN_PROPERTY]).mae = group.mean ?? null;
  });
  (groups?.bias?.groups || []).forEach((group) => {
    at(group[DEPTH_BIN_PROPERTY]).bias = group.mean ?? null;
  });

  return [...byBin.values()].sort((a, b) => a.bin - b.bin);
};

/**
 * @function errorByDepthBin
 * @description The validation error broken down by depth band: how many points fell in each band,
 * and the RMSE, MAE and bias within it.
 * The three statistics are grouped separately and sent in a single dictionary, so each reduction
 * stays readable at the cost of nothing - it is one round trip either way.
 * @param {Object} collection - Paired points already carrying the residual columns, as the metrics
 *  report builds them.
 * @param {Object} [options]
 * @param {Number} [options.binSize] - Band width, in metres.
 * @param {String} [options.depthProperty] - Column holding the reference depth.
 * @param {String} [options.squaredProperty] - Column holding the squared residual.
 * @param {String} [options.absoluteProperty] - Column holding the absolute residual.
 * @param {String} [options.biasProperty] - Column holding the signed residual.
 * **Called by:
 * @see {computeMetrics} - at the end of a run.
 * @memberof module:Bathymetry
 * @returns {Promise<Array<Object>>} As {@link mergeDepthBinGroups} returns them; empty when the
 *  reduction fails, since a missing breakdown must not cost the user the analysis.
 */
export const errorByDepthBin = async (
  collection,
  {
    binSize = DEPTH_BIN_SIZE,
    depthProperty = 'or_depth',
    squaredProperty = 'sqdiff',
    absoluteProperty = 'absdiff',
    biasProperty = 'diff',
  } = {},
) => {
  const binned = withDepthBin(collection, binSize, depthProperty);
  const grouped = (property, reducer) =>
    binned.reduceColumns({
      reducer: reducer.group({ groupField: 1, groupName: DEPTH_BIN_PROPERTY }),
      selectors: [property, DEPTH_BIN_PROPERTY],
    });

  const request = ee.Dictionary({
    squared: grouped(squaredProperty, ee.Reducer.mean().combine({ reducer2: ee.Reducer.count(), sharedInputs: true })),
    absolute: grouped(absoluteProperty, ee.Reducer.mean()),
    bias: grouped(biasProperty, ee.Reducer.mean()),
  });

  try {
    const groups = await new Promise((resolve, reject) => {
      request.evaluate((result, error) => (error ? reject(error) : resolve(result)));
    });
    return mergeDepthBinGroups(groups, binSize);
  } catch (error) {
    console.error('[validation] the error by depth band could not be computed:', error);
    return [];
  }
};
